import React, { useEffect, useState } from "react";
import {
  Form, Input, DatePicker, TimePicker, Select, Mentions, Button, message,
  Checkbox, Radio, Modal
 } from 'antd';
import timeZones from '../../utils/live-schedules/drop-down-tables/timeZones';
import assignmentStatusList from '../../utils/live-schedules/drop-down-tables/assignmentStatusList';
import assignmentPriceMethodList from '../../utils/live-schedules/drop-down-tables/assignmentPriceMethodList';
import FindCostObject from '../../SelectCostObject/FindCostObject';
import AuthLogic from '../../../../AuthLogic';
import './forms.scss';

const { TextArea } = Input;
const { Option } = Select;

const datePickerFormat = 'h:mm a, MMM Do'
const durationFormat = 'HH:mm';
const FormItem = Form.Item;

const AddAssignmentForm = React.memo(({ loading, resource, itemDetails, user, confirmDialogue,
  relevantJobs, relevantJobsForCostObject, relatedBranches, groupsByBranchAndResource,
  servicesByDate, activityTypes, serviceSalaryLevel, servicePrimaryRate, serviceJobSalaries, ...props
}) => {
  const { itemType } = itemDetails;
  const { getFieldDecorator, getFieldsError, getFieldsValue } = props.form;
  const [showFindCostObjectForFilterModal, setShowFindCostObjectForFilterModal] = useState(false);
  const [showFindCostObjectForSelectionModal, setShowFindCostObjectForSelectionModal] = useState(false);
  const [filterRequestsByCostObject, setFilterRequestsByCostObject] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const invalidForm = () => {
    message.config({
      top: 150,
      duration: 5,
      maxCount: 5,
    });
    message.error('You have one or more invalid form inputs!');
  };

  const handleSubmit = async (e) => {
    setFormSubmitted(true);
    e.preventDefault();
    const errors = {};
    // collect all fields currently in error state
    for ( const [field, error] of Object.entries(getFieldsError()) ) {
      if (error) {
        errors[`${field}`] = error;
      }
    }
    if (Object.values(errors).length === 0) {
      console.log('Success! Received values of form: ', getFieldsValue())
      props.createAssignment();
    } else {
      invalidForm();
      console.log('Invalid Form! ', errors);
    }
  };

  const validateAssignmentStartTime = (rule, value, callback) => {
    const form = props.form;
    const assignmentEndTime = form.getFieldValue('assignmentEndTime');
    // *** Note: Not necessary since we're adjusting tourEndTime whenever a change in tourStartTime field
    // if (tourEndTime.isBefore(value)) {
    //   callback('Tour start time cannot occur after tour end time!');
    // } else {
    //   callback();
    // }
  }

  const validateAssignmentEndTime = (rule, value, callback) => {
    const form = props.form;
    const assignmentStartTime = form.getFieldValue('assignmentStartTime');
    if (assignmentStartTime.isAfter(value)) {
      callback('Assignment end time cannot occur before assignment start time!');
    } else {
      callback();
    }
  }

  const validateJobTitleField = (rule, value, callback) => {
    const form = props.form;
    if (value.length > 50) {
      callback('Maximum length is 50 characters!')
    } else {
      callback();
    }
  }

  const validateCommentField = (rule, value, callback) => {
    const form = props.form;
    if (value.length > 1000) {
      callback('Maximum length is 1000 characters!')
    } else {
      callback();
    }
  }

  /**
   * Filter Job Requests by Cost Object
   */
  const handleFilterByCostObjectClick = () => {
    if (!itemDetails.assignmentStartTime) {
      noStartTimeWarning();
    } else {
      setShowFindCostObjectForFilterModal(true);
    }
  }

  const handleSelectCostObjectFilter = (record) => {
    console.log(record); // debug
    setFilterRequestsByCostObject(true);
    props.handleCostObjectFilterChange('update', record);
  }

  const handleFilterByCostObjectModalCancel = () => {
    setShowFindCostObjectForFilterModal(false);
  }

  const handleClearCostObjectFilter = () => {
    setFilterRequestsByCostObject(false);
    props.handleCostObjectFilterChange('delete');

  }

  /**
   * Select a Cost Object for the Job/Job Element
   */
  const handleSelectCostObjectClick = () => {
    if (!itemDetails.assignmentStartTime) {
      noStartTimeWarning();
    } else {
      setShowFindCostObjectForSelectionModal(true);
    }
  }

  const handleSelectCostObject = (record) => {
    // input costObjectTypeId field value
    record.costObjectTypeId = record.costObjectType === 'COST_CENTER' ? 2 : 1; // TODO: fix findCostObject so PROJECT_DEFINITION isn't possible
    itemDetails.costObject = record;
    props.updateState({itemDetails});
  }

  const handleSelectCostObjectModalCancel = () => {
    setShowFindCostObjectForSelectionModal(false);
  }

  const noStartTimeWarning = () => {
    Modal.warning({
      zIndex:3000,
      title: <p>No Start Time</p>,
      content: <p>Please select a start time to filter requests by cost object.</p>,
      onOk() {
        // just close modal
      },
    });
  }

    /**
     * populate fields on initial component mount
     * (getFieldDecorator overrides `value` on forms)
     */
    useEffect(() => {
      const form = props.form;
      if (relatedBranches) {
        form.setFieldsValue({
          assignmentStartTime: itemDetails.assignmentStartTime,
          assignmentDuration:  itemDetails.assignmentDuration,
          assignmentEndTime: itemDetails.assignmentEndTime,
          priceMethodId: itemDetails.priceMethodId
        });
        // clear job dropdowns (no start date selected yet)
        relevantJobs = null;
        relevantJobsForCostObject = null;
        props.updateState({
          relevantJobs,
          relevantJobsForCostObject
        });
      }
    }, [relatedBranches, itemDetails.assignmentDuration, confirmDialogue]);

    // jobId dropdown hook
    useEffect(() => {
      // job selection is dependent on assignmentStartTime
      if (itemDetails.assignmentStartTime) {
        if (!filterRequestsByCostObject) {
          props.getRelevantJobs(); // update dropdown contents
        }
        else if (filterRequestsByCostObject && itemDetails.costObjectFilter) {
          props.getRelevantJobsForCostObject();
        }
      } else {
        // clear jobId field and dropdown lists
        props.handleOmnibusFieldChange(`jobId-${null}`);
        relevantJobs = null;
        relevantJobsForCostObject = null;
        props.updateState({
          relevantJobs,
          relevantJobsForCostObject
        });
      }
    },[itemDetails.assignmentStartTime, itemDetails.assignmentDuration, filterRequestsByCostObject, itemDetails.costObjectFilter]);

    // groupId dropdown hook
    useEffect(() => {
      // group selection is dependent on executingBranchId
      if (itemDetails.executingBranchId && itemDetails.executingBranchId !== 'null') {
        props.handleOmnibusFieldChange(`serviceId-${null}`);
        props.getGroupsByBranchAndResource();
      } else {
        // clear groupId field and groupsByBranchAndResource dropdown list
        props.handleOmnibusFieldChange(`groupId-${null}`);
        groupsByBranchAndResource = null;
        props.updateState({itemDetails});
      }
    }, [itemDetails.executingBranchId, itemDetails.groupId, itemDetails.assignmentStartTime]);

    // serviceId dropdown hook
    useEffect(() => {
      // service selection is dependent on groupId, assignment start + end time, branchId, and priceMethodId
      if (itemDetails.groupId && itemDetails.groupId !== 'null' && itemDetails.assignmentStartTime
      && itemDetails.assignmentEndTime && itemDetails.executingBranchId && itemDetails.executingBranchId !== 'null'
      && itemDetails.priceMethodId && itemDetails.priceMethodId !== 'null'
         ) {
        props.getServicesByDate();
      } else {
        // clear serviceId and relatedServiceId fields and servicesByDate dropdown list
        props.handleOmnibusFieldChange(`serviceId-${null}`);
        props.handleOmnibusFieldChange(`relatedServiceId-${null}`);
        servicesByDate = null;
        props.updateState({servicesByDate});
      }
    }, [itemDetails.groupId, itemDetails.assignmentStartTime, itemDetails.assignmentEndTime,
        itemDetails.executingBranchId, itemDetails.priceMethodId, itemDetails.serviceId]);

    // activityType dropdown hook
    useEffect(() => {
      // activityType is dependent on assignmentStartTime and relatedServiceId
      if (itemDetails.assignmentStartTime && itemDetails.relatedServiceId
        && itemDetails.relatedServiceId !== 'null') {
        props.getActivityTypes();
      } else {
        // clear activityType field and activityTypes dropdown list
        props.handleOmnibusFieldChange(`activityType-${null}`);
        activityTypes = null;
        props.updateState({activityTypes});
      }
    }, [itemDetails.assignmentStartTime, itemDetails.relatedServiceId, itemDetails.activityType]);

    // service serviceSalaryLevel and servicePrimaryRate dropdown hook
    useEffect(() => {
      if (itemDetails.serviceId && itemDetails.serviceId !== 'null') {
        props.getServiceSalaryLevel();
        props.getServicePrimaryRate();
      } else {
        // clear serviceSalaryLevel and servicePrimaryRate fields
        props.handleOmnibusFieldChange(`servicePrimaryRate-${null}`);
        serviceSalaryLevel = null;
        servicePrimaryRate = null;
        props.updateState({
          serviceSalaryLevel,
          servicePrimaryRate
        })
      }
    }, [itemDetails.serviceId, itemDetails.servicePrimaryRate]);

    // jobSalaryUpgradeId dropdown hook
    useEffect(() => {
      if (itemDetails.assignmentStartTime && itemDetails.serviceId
        && itemDetails.serviceId !== 'null') {
        props.getJobSalariesByService();
      } else {
        // clear jobSalaryUpgradeId field
        props.handleOmnibusFieldChange(`jobSalaryUpgradeId-${0}`);
        serviceJobSalaries = null;
        props.updateState({serviceJobSalaries});
      }
    }, [itemDetails.assignmentStartTime, itemDetails.serviceId, itemDetails.servicePrimaryRate]);



  if (relatedBranches) {
    console.log(itemDetails); // debug
    return (

          <Form onSubmit={handleSubmit}>
            <FormItem
                  label="Resource Name"
                  style={{ display: "flex", marginBottom: 5 }}
            >
                <Input
                  name="resourceName"
                  value={resource.name}
                  disabled
                  size={'large'}
                  placeholder={''}/>
          </FormItem>

            <FormItem
            label="Time Zone"
            style={{ display: "flex", marginBottom: 5 }}
            >
              <Select
                name="timeZone"
                value={`timeZoneId-${itemDetails.timeZoneId}`}
                onChange={props.handleOmnibusFieldChange}
                style={{ width: 110 }}
              >
                {timeZones.map((timeZone) => {
                  return <Option key={timeZone.id} value={`timeZoneId-${timeZone.id}`}>{timeZone.name}</Option>
                })}
              </Select>
            </FormItem>

              <FormItem
                label="Start Time"
                style={{ display: "flex", marginBottom: 5 }}
              >
                {getFieldDecorator('assignmentStartTime', {
                      rules: [{
                          required: true, message: 'Please input start time!',
                      }, {
                          validator: validateAssignmentStartTime,
                      }],
                  })(
                      <DatePicker
                        name="assignmentStartTime"
                        showTime={{ format: datePickerFormat, use12Hours: true, minuteStep: 15 }}
                        onChange={props.handleItemStartTimeChange}
                        placeholder={'Select date and time'}
                        format={datePickerFormat}
                        allowClear={false}
                        size={'large'}
                        popupStyle={{ zIndex: 2000 }}
                      />
                  )}
              </FormItem>

              <FormItem
                label="Duration"
                style={{ display: "flex", marginBottom: 5 }}
              >
                {  itemDetails.assignmentDuration && itemDetails.assignmentDurationDays && itemDetails.assignmentDurationDays.days &&
                  (
                    <span>
                      <Mentions
                        value={itemDetails.assignmentDurationDays.days}
                        readOnly
                        style={{ marginLeft: 10, marginRight: 5, width: 50 }}
                      />
                      <span>day(s) &</span>
                    </span>
                  )
                }
                  {getFieldDecorator('assignmentDuration', {
                  rules: [{
                      required: true, message: 'Duration is required!',
                      type: 'object'
                  }],
                    })(
                        <TimePicker
                          name="assignmentDuration"
                          onChange={props.handleItemDurationChange}
                          allowClear={false}
                          format={durationFormat}
                          minuteStep={15}
                          style={{ marginLeft: 5, marginRight: 5 }}
                        />
                  )}
                hours
              </FormItem>

              <FormItem
                label="End Time"
                style={{ display: "flex", marginBottom: 5 }}
              >
                {getFieldDecorator('assignmentEndTime', {
                      rules: [{
                          required: true, message: 'Please input end time!',
                      }, {
                          validator: validateAssignmentEndTime,
                      }],
                  })(
                      <DatePicker
                        name="assignmentEndTime"
                        showTime={{ format: datePickerFormat, use12Hours: true, minuteStep: 15 }}
                        onChange={props.handleItemEndTimeChange}
                        placeholder={'Select date and time'}
                        format={datePickerFormat}
                        size={'large'}
                        popupStyle={{ zIndex: 2000 }}
                      />
                  )}
              </FormItem>

              <a
                onClick={() => handleFilterByCostObjectClick()}
                style={{ color: 'dodgerblue', textDecoration: 'underline', cursor: 'pointer' }}
              >
                Filter Requests by Cost Object:
              </a>

                {`\xa0`}
                { filterRequestsByCostObject &&
                `${itemDetails.costObjectFilter.costObjectName}
                ${`\xa0`}
                ${itemDetails.costObjectFilter.costObjectType === 'WBS_ELEMENT'
                || itemDetails.costObjectFilter.costObjectType === 'PROJECT_DEFINITION'
                ? 'WBS Element' : 'Not specified'}`}
                {`\xa0`}

                { filterRequestsByCostObject &&
                    <Button
                      type="primary"
                      ghost
                      onClick={handleClearCostObjectFilter}
                    >
                      Clear
                    </Button>
                }

               { showFindCostObjectForFilterModal &&
                <Modal
                  visible={showFindCostObjectForFilterModal}
                  title={'Find Cost Object'}
                  onCancel={handleFilterByCostObjectModalCancel}
                  footer={[
                  ]}
                >
                  <FindCostObject
                    requestDate={itemDetails.assignmentStartTime.format()}
                    handleSelectCostObject={handleSelectCostObjectFilter}
                    handleModalCancel={handleFilterByCostObjectModalCancel}
                  />
                </Modal>
              }

            <FormItem
                // no room for label here, see below
                style={{ display: "flex", marginBottom: 5 }}
            >
                <span id="asterisk">* </span><span style={{color: 'black'}}>Today's Valid Requests:</span>

                <Select
                  name="jobId"
                  defaultValue={`jobId-${null}`}
                  value={`jobId-${itemDetails.jobId}`}
                  onChange={(e) => {
                    props.handleOmnibusFieldChange(e);
                  // also update jobNumber field from same record in dropdown
                    if (e.split('-')[1] !== 'null') {
                      const id = parseInt(e.split('-')[1]);
                      let jobArray = !filterRequestsByCostObject ? relevantJobs : relevantJobsForCostObject;
                      let { jobNumber } = jobArray.find((elem) => elem.id === id);
                      props.handleOmnibusFieldChange(`jobNumber-${jobNumber}`);
                    } else {
                      props.handleOmnibusFieldChange(`jobNumber-${null}`);
                    }
                  }}
                  clearable
                  disabled={!itemDetails.assignmentStartTime}
                  style={!itemDetails.assignmentStartTime ? { width: 300, marginLeft: 5 } : { width: 475 }}
                >
                  <Option key={null} value={`jobId-${null}`}>{itemDetails.assignmentStartTime ? 'New Request' : 'Please enter a start time above'}</Option>

                  { itemDetails.assignmentStartTime && relevantJobs &&
                    !filterRequestsByCostObject && relevantJobs.map((elem) => {
                      return <Option key={elem.id} value={`jobId-${elem.id}`}>{elem.name}</Option>
                    })
                  }

                  { itemDetails.assignmentStartTime && relevantJobsForCostObject &&
                    filterRequestsByCostObject && relevantJobsForCostObject.map((elem) => {
                      return <Option key={elem.id} value={`jobId-${elem.id}`}>{elem.name}</Option>
                    })
                  }
                </Select>
              </FormItem>

            { (!itemDetails.jobId || itemDetails.jobId === 'null') &&   // not selecting from existing jobId, hence new title
              <FormItem
                label={<span><span ID="asterisk">* </span><span>Request Title (Slug)</span></span>}
                style={{ display: "flex", marginBottom: 5 }}
              >
                  {getFieldDecorator('jobTitle', {
                      rules: [{
                          validator: validateJobTitleField,
                      }],
                  })(
                    <Input
                      name="jobTitle"
                      onChange={props.handleOmnibusFieldChange}
                      style={{ width: 300 }} />
                  )}
                </FormItem>
              }

            <FormItem
            label={<span><span ID="asterisk">* </span><span>Executing Branch</span></span>}
            style={{ display: "flex", marginBottom: 5 }}
            >
              <Select
                name="executingBranchId"
                value={`executingBranchId-${itemDetails.executingBranchId}`}
                onChange={props.handleOmnibusFieldChange}
                style={{ width: 250 }}
              >
                <Option key={null} value={`executingBranchId-${null}`}>&nbsp;</Option>

                { relatedBranches &&
                  relatedBranches.map((elem) => {
                  return <Option key={elem.id} value={`executingBranchId-${elem.id}`}>{elem.name}</Option>
                })}
              </Select>
            </FormItem>

            <FormItem
            label={<span><span ID="asterisk">* </span><span>Group</span></span>}
            style={{ display: "flex", marginBottom: 5 }}
            >
              <Select
                name="groupId"
                defaultValue={`groupId-${null}`}
                value={`groupId-${itemDetails.groupId}`} // necessary to clear value dynamically
                disabled={!itemDetails.executingBranchId || itemDetails.executingBranchId === 'null'}
                onChange={props.handleOmnibusFieldChange}
                style={{ width: 230 }}
              >
                 <Option key={null} value={`groupId-${null}`}>&nbsp;</Option>

                { groupsByBranchAndResource &&
                  groupsByBranchAndResource.map((elem) => {
                  return <Option key={elem.id} value={`groupId-${elem.id}`}>{elem.name}</Option>
                })}
              </Select>
            </FormItem>

            <FormItem
            label={<span><span ID="asterisk">* </span><span>Service</span></span>}
            style={{ display: "flex", marginBottom: 5 }}
            >
              <Select
                name="serviceId"
                value={`serviceId-${itemDetails.serviceId}`}
                disabled={!itemDetails.executingBranchId || itemDetails.executingBranchId === 'null' ||
                  !itemDetails.assignmentStartTime || !itemDetails.groupId || itemDetails.groupId === 'null' }
                onChange={(e) => {
                  props.handleOmnibusFieldChange(e);
                  // also update relatedServiceId field from same record in dropdown
                  if (e.split('-')[1] !== 'null') {
                    const id = parseInt(e.split('-')[1]);
                    const { relatedServiceId, name } = servicesByDate.find((elem) => elem.id === id);
                    props.handleOmnibusFieldChange(`relatedServiceId-${relatedServiceId}`);
                    props.handleOmnibusFieldChange(`serviceName-${name.split(' 1 ')[0].trim()}`);
                  } else {
                    props.handleOmnibusFieldChange(`relatedServiceId-${null}`);
                    props.handleOmnibusFieldChange(`serviceName-${null}`);
                  }
                }}
                style={{ width: 400 }}
              >
                <Option key={null} value={`serviceId-${null}`}>{itemDetails.assignmentStartTime ? `\xa0` : 'Please enter a start time above'}</Option>


                { servicesByDate &&
                  servicesByDate.map((elem) => {
                  return <Option key={elem.id} value={`serviceId-${elem.id}`}>{elem.name}</Option>
                })}
              </Select>
            </FormItem>

            <FormItem
            label={<span><span ID="asterisk">* </span><span>Activity Type</span></span>}
            style={{ display: "flex", marginBottom: 5 }}
            >
              <Select
                name="activityType"
                // should only have `None` as option unless a Service is selected
                value={`activityType-${itemDetails.activityType}`}
                disabled={!itemDetails.assignmentStartTime}
                onChange={props.handleOmnibusFieldChange}
                style={{ width: 350 }}
              >
                <Option key={null} value={`activityType-${null}`}>{itemDetails.assignmentStartTime ? `None` : 'Please enter a start time above'}</Option>

                { activityTypes &&
                  activityTypes.map((elem) => {
                  return <Option key={elem.activityType} value={`activityType-${elem.activityType.trim()}`}>{elem.description.split('VIDEO')[0] + elem.rates}</Option>
                })}
              </Select>
            </FormItem>

            { AuthLogic.addAssignmentViewSalaryLevel(user) &&
              <FormItem
              label="Salary Level"
              style={{ display: "flex", marginBottom: 5 }}
              >
                {
                  serviceSalaryLevel && serviceSalaryLevel !== 'null' ? serviceSalaryLevel : ' '
                }
              </FormItem>
            }

            {/* TODO: DELETE */}
            {/* <FormItem
                label="Salary Level Upgrade"
                style={{ display: "flex", marginBottom: 5 }}
            >
                <Radio
                  name="isAnUpgrade"
                  onChange={props.handleOmnibusFieldChange}
                  size={'large'}
                />
              </FormItem> */}

              <FormItem
                label="" // leave blank
                style={{ display: "flex", marginBottom: 5 }}
              >
                  <Select
                    name="jobSalaryUpgradeId"
                    onChange={props.handleOmnibusFieldChange}
                    style={{ width: 120, marginBottom: 5 }}
                  >
                    { serviceJobSalaries &&
                      serviceJobSalaries.map((elem) => {
                      return (
                          <Option
                            style={{ color: elem.primaryRate <= servicePrimaryRate ? 'red' : 'black' }}
                            key={elem.id} value={`jobSalaryUpgradeId-${elem.id}`}
                          >
                            {elem.name}
                          </Option>
                      )
                    })}
                  </Select>

              </FormItem>

            <FormItem
                label="Assignment is upgraded"
                style={{ display: "flex", marginBottom: 5 }}
              >
                <Checkbox
                  name="upgrade"
                  // onChange={props.handleOmnibusFieldChange}
                  checked={false}
                  size={'large'}
                />
              </FormItem>

            <FormItem
            label="Price Method"
            style={{ display: "flex", marginBottom: 5 }}
            >
              <Select
                name="priceMethodId"
                defaultValue={`priceMethodId-${user.securityLevels.defaultPriceMethod}`} // default is Rate Card = 2
                onChange={props.handleOmnibusFieldChange}
                style={{ width: 120 }}
              >
                {assignmentPriceMethodList.map((elem) => {
                  return <Option key={elem.id} value={`priceMethodId-${elem.id}`}>{elem.name}</Option>
                })}
              </Select>
            </FormItem>

            { AuthLogic.addAssignmentEditAssignmentStatus(user) &&
              <FormItem
              label="Status"
              style={{ display: "flex", marginBottom: 5 }}
              >
                <Select
                  name="status"
                  value={`status-${itemDetails.status}`}
                  onChange={props.handleOmnibusFieldChange}
                  style={{ width: 120 }}
                >
                  {assignmentStatusList.map((status) => {
                    return <Option key={status.id} value={`status-${status.id}`}>{status.name}</Option>
                  })}
                </Select>
              </FormItem>
            }

            <span ID="asterisk">* </span>
            <a
                onClick={() => handleSelectCostObjectClick()}
                style={{ color: 'dodgerblue', textDecoration: 'underline', cursor: 'pointer' }}
              >
                Cost Object:
            </a>

                {`\xa0`}
                { itemDetails.costObject &&
                `${itemDetails.costObject.costObjectName}
                ${`\xa0`}
                ${itemDetails.costObject.costObjectType === 'WBS_ELEMENT'
                || itemDetails.costObject.costObjectType === 'PROJECT_DEFINITION'
                ? 'WBS Element' : 'Not specified'}`}
                {`\xa0`}

               { showFindCostObjectForSelectionModal &&
                <Modal
                  visible={showFindCostObjectForSelectionModal}
                  title={'Find Cost Object'}
                  onCancel={handleSelectCostObjectModalCancel}
                  footer={[
                  ]}
                >
                  <FindCostObject
                    requestDate={itemDetails.assignmentStartTime.format()}
                    handleSelectCostObject={handleSelectCostObject}
                    handleModalCancel={handleSelectCostObjectModalCancel}
                  />
                </Modal>
              }

            { AuthLogic.addAssignmentEditDescription(user) &&
              <FormItem
                    label="Description"
                    style={{ display: "flex", marginBottom: 5 }}
              >
                {getFieldDecorator('descriptionComment', {
                    rules: [{
                        validator: validateCommentField,
                    }],
                })(
                  <TextArea
                    name="descriptionComment"
                    onChange={props.handleOmnibusFieldChange}
                    size={'large'}
                    placeholder={'Enter text here.'}
                    style={{ width: 300 }} />
                )}
              </FormItem>
            }

              <FormItem
                label="Overtime"
                style={{ display: "flex", marginBottom: 5 }}
              >
                <Checkbox
                  name="overtime"
                  onChange={props.handleOmnibusFieldChange}
                  size={'large'}
                />
              </FormItem>

              <FormItem
                style={{ marginTop: 20 }}
              >
                <Button type="primary" ghost block size={'large'} htmlType="submit" disabled={formSubmitted} >
                  {!formSubmitted ? 'Save' : 'Saving...'}
                </Button>
              </FormItem>
          </Form>
      );
  } else return 'loading...'
})

const WrappedAddAssignmentForm = Form.create()(AddAssignmentForm);

export default WrappedAddAssignmentForm;




// WEBPACK FOOTER //
// ./src/Components/Schedule/LiveScheduleUpdate/Forms/AddAssignmentForm.js